'use client';

// ============================================================================
// GLOBAL WORK ITEM OPENER CONTEXT
// Opens any work item from anywhere in the portal via WorkItemDetailModal.
//
// Usage:
//   const { openWorkItem, openWorkItemByNumber } = useWorkItemOpener();
//   openWorkItem('uuid-here');
//   openWorkItemByNumber('JOB-0042');
//
// Permalink support:
//   Any URL + ?wi=JOB-0042 or ?wi=uuid → auto-opens the modal on page load.
//
// This context is mounted at the client-zone layout level so it's available
// everywhere within the authenticated portal.
// ============================================================================

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  Suspense,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { authService } from '@/lib/authService';
import { isUuid } from '@/lib/uuid';
import type { WorkItem } from '@/components/UniversalWorkItems';
import type { WorkItemTabId } from '@/components/WorkItemDetailModal';
import type { Document, DocumentType } from '@/hooks/useDocumentsWorkItems';
import { DOCUMENT_CONFIGS } from '@/components/finance/modals/documentConfigs';
import { emit, on } from '@/lib/typedEventBus';

// Lazy-load WorkItemDetailModal to avoid bundling it on every page
const WorkItemDetailModal = dynamic(
  () => import('@/components/WorkItemDetailModal'),
  { ssr: false }
);

const FinanceDocumentModal = dynamic(
  () => import('@/components/finance/modals/DocumentModal').then((mod) => mod.DocumentModal),
  { ssr: false }
);

type UnknownRecord = Record<string, unknown>;
type FinanceDocumentWorkItem = WorkItem & { item_type: DocumentType };

const FINANCE_DOCUMENT_TYPE_SET = new Set<DocumentType>(
  Object.keys(DOCUMENT_CONFIGS) as DocumentType[],
);

const WORK_ITEM_DETAIL_FINANCE_TYPES = new Set<DocumentType>(['proposal']);

function isUnknownRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: UnknownRecord, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }

  return undefined;
}

function readNumber(record: UnknownRecord, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
}

function toFinanceDocumentType(value: unknown): DocumentType | null {
  if (typeof value !== 'string') {
    return null;
  }

  return FINANCE_DOCUMENT_TYPE_SET.has(value as DocumentType)
    ? (value as DocumentType)
    : null;
}

function isFinanceDocumentWorkItem(workItem: WorkItem | null | undefined): workItem is FinanceDocumentWorkItem {
  const financeDocumentType = toFinanceDocumentType(workItem?.item_type);
  return financeDocumentType !== null && !WORK_ITEM_DETAIL_FINANCE_TYPES.has(financeDocumentType);
}

function mapWorkItemToFinanceDocument(workItem: FinanceDocumentWorkItem): Document {
  const record = workItem as unknown as UnknownRecord;
  const docType = workItem.item_type;
  const docNumber = readString(record, 'doc_number', 'item_number') || 'Draft';

  return {
    id: String(workItem.id),
    client_id: readNumber(record, 'client_id'),
    doc_type: docType,
    item_type: docType,
    doc_number: docNumber,
    item_number: readString(record, 'item_number', 'doc_number') || docNumber,
    title: readString(record, 'title'),
    description: readString(record, 'description'),
    status: (readString(record, 'status') as Document['status'] | undefined) || 'draft',
    issued_at: readString(record, 'issued_at', 'created_at', 'updated_at') || new Date().toISOString(),
    due_date: readString(record, 'due_date'),
    expiry_date: readString(record, 'expiry_date', 'valid_until'),
    valid_until: readString(record, 'valid_until', 'expiry_date'),
    delivery_date: readString(record, 'delivery_date'),
    contact_id: readNumber(record, 'contact_id'),
    contact_name: readString(record, 'contact_name'),
    contact_email: readString(record, 'contact_email'),
    contact_phone: readString(record, 'contact_phone'),
    billing_address: readString(record, 'billing_address', 'location_address'),
    shipping_address: readString(record, 'shipping_address'),
    location_id: readNumber(record, 'location_id'),
    location_name: readString(record, 'location_name'),
    location_address: readString(record, 'location_address'),
    contact_person_id: readNumber(record, 'contact_person_id'),
    contact_person_name: readString(record, 'contact_person_name', 'contact_person'),
    currency: readString(record, 'currency') || 'ZAR',
    exchange_rate: readNumber(record, 'exchange_rate'),
    subtotal: readNumber(record, 'subtotal') || 0,
    tax_total: readNumber(record, 'tax_total') || 0,
    discount_amount: readNumber(record, 'discount_amount'),
    discount_percentage: readNumber(record, 'discount_percentage'),
    grand_total: readNumber(record, 'grand_total') || 0,
    amount_paid: readNumber(record, 'amount_paid'),
    balance_due: readNumber(record, 'balance_due'),
    payment_terms: readNumber(record, 'payment_terms'),
    payment_status: readString(record, 'payment_status') as Document['payment_status'] | undefined,
    paid_at: readString(record, 'paid_at'),
    notes: readString(record, 'notes'),
    terms_and_conditions: readString(record, 'terms_and_conditions'),
    internal_notes: readString(record, 'internal_notes'),
    reference: readString(record, 'reference'),
    created_at: readString(record, 'created_at'),
    updated_at: readString(record, 'updated_at'),
    created_by: readNumber(record, 'created_by'),
    custom_fields: isUnknownRecord(record.custom_fields)
      ? record.custom_fields
      : undefined,
    share_token: readString(record, 'share_token'),
    customer_po_number: readString(record, 'customer_po_number'),
    supplier_reference: readString(record, 'supplier_reference'),
    expected_delivery_date: readString(record, 'expected_delivery_date'),
    delivery_instructions: readString(record, 'delivery_instructions'),
    shipping_method: readString(record, 'shipping_method'),
    shipping_cost: readNumber(record, 'shipping_cost'),
    fulfillment_status: readString(record, 'fulfillment_status'),
    quantity_received_percentage: readNumber(record, 'quantity_received_percentage'),
    parent_document_id: readString(record, 'parent_document_id', 'parent_id'),
    parent_id: readString(record, 'parent_id', 'parent_document_id'),
    batch_id: readString(record, 'batch_id') || null,
    batch_number: readString(record, 'batch_number') || null,
    batch_status: readString(record, 'batch_status') || null,
  };
}

// ============================================================================
// TYPES
// ============================================================================

export interface WorkItemOpenerOptions {
	/** Preferred tab to show when the modal opens */
	initialTab?: WorkItemTabId;
  /** Override the visible tabs for the modal */
  visibleTabs?: WorkItemTabId[];
  /** Override the modal title */
  title?: string;
  /** Callback when the work item is closed */
  onClose?: () => void;
  /** Callback to refresh the parent view after changes */
  onRefresh?: () => void;
  /** Callback for status changes */
  onStatusChange?: (workItemId: string, newStatus: string, workItem: WorkItem) => void;
  /**
   * Parent work-items list scope (`WorkItemsViewWrapper.moduleContext`): operational `template_key`
   * or industry `solution_key` (e.g. service-operations, field-services).
   */
  listModuleContext?: string | null;
  /** Active solution key for filtering assignees by team membership */
  activeSolutionKey?: string | null;
}

interface WorkItemOpenerContextValue {
  /** Open a work item by its UUID */
  openWorkItem: (id: string, options?: WorkItemOpenerOptions) => void;
  /** Open a work item by its human-readable number (e.g., JOB-0042) */
  openWorkItemByNumber: (itemNumber: string, options?: WorkItemOpenerOptions) => void;
  /** Open a pre-loaded work item object directly */
  openWorkItemDirect: (workItem: WorkItem, options?: WorkItemOpenerOptions) => void;
  /** Close the current work item modal */
  closeWorkItem: () => void;
  /** Whether a work item is currently open */
  isOpen: boolean;
  /** The currently open work item (if any) */
  currentWorkItem: WorkItem | null;
  /** Whether loading is in progress */
  isLoading: boolean;
  /** Generate a permalink URL for a work item */
  getPermalink: (workItemIdOrNumber: string) => string;
}

interface WorkItemModalStackEntry {
  key: string;
  workItem: WorkItem | null;
  isLoading: boolean;
  error: string | null;
  options: WorkItemOpenerOptions;
  requestedTarget?: string;
}

const MAX_OPEN_WORK_ITEM_MODALS = 2;

const WorkItemOpenerContext = createContext<WorkItemOpenerContextValue | undefined>(undefined);

// ============================================================================
// INNER COMPONENT (uses useSearchParams — needs Suspense boundary)
// ============================================================================

function WorkItemOpenerInner({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // State
  const [modalStack, setModalStack] = useState<WorkItemModalStackEntry[]>([]);
  const modalStackCounterRef = useRef(0);
  const modalStackRef = useRef<WorkItemModalStackEntry[]>([]);

  const renderPortal = useCallback((node: React.ReactNode) => {
    if (typeof document === 'undefined') {
      return null;
    }

    return createPortal(node, document.body);
  }, []);

  // Ref to prevent double-opening from URL params
  const handledWiParam = useRef<string | null>(null);

  useEffect(() => {
    modalStackRef.current = modalStack;
  }, [modalStack]);

  // ============================================================================
  // FETCH WORK ITEM
  // ============================================================================

  const fetchWorkItem = useCallback(async (idOrNumber: string): Promise<{ item: WorkItem | null; error: string | null }> => {
    const token = authService.getTokens()?.access_token;
    if (!token) {
      console.error('[WorkItemOpener] No auth token available');
      return { item: null, error: 'No auth token available' };
    }

    const paramKey = isUuid(idOrNumber) ? 'id' : 'number';
    const url = `/api/v1/work-items/lookup?${paramKey}=${encodeURIComponent(idOrNumber)}`;

    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Failed to fetch work item (${response.status})`);
      }

      const data = await response.json();
      if (data.success && data.workItem) {
        return { item: data.workItem as WorkItem, error: null };
      }
      throw new Error('Work item not found');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch work item';
      console.error('[WorkItemOpener] Fetch error:', message);
      return { item: null, error: message };
    }
  }, []);

  const createModalEntryKey = useCallback(() => {
    modalStackCounterRef.current += 1;
    return `work-item-modal-${modalStackCounterRef.current}`;
  }, []);

  const normalizeTargetValue = useCallback((value: string) => value.trim().toLowerCase(), []);

  const createRequestedTarget = useCallback((kind: 'id' | 'number' | 'direct', value: string) => {
    return `${kind}:${normalizeTargetValue(value)}`;
  }, [normalizeTargetValue]);

  const getTopModalEntry = useCallback(() => {
    const currentStack = modalStackRef.current;
    return currentStack[currentStack.length - 1] || null;
  }, []);

  const pushModalEntry = useCallback((entry: WorkItemModalStackEntry) => {
    setModalStack((previousStack) => {
      const withoutCurrent = previousStack.filter((currentEntry) => currentEntry.key !== entry.key);
      const trimmedStack = withoutCurrent.slice(-(MAX_OPEN_WORK_ITEM_MODALS - 1));
      return [...trimmedStack, entry];
    });
  }, []);

  const updateModalEntry = useCallback((key: string, updates: Partial<WorkItemModalStackEntry>) => {
    setModalStack((previousStack) => previousStack.map((entry) => (
      entry.key === key ? { ...entry, ...updates } : entry
    )));
  }, []);

  const reuseTopEntryIfSameTarget = useCallback((params: {
    requestedTarget: string;
    workItemId?: string | null;
    workItemNumber?: string | null;
    options: WorkItemOpenerOptions;
    workItem?: WorkItem | null;
    isLoading?: boolean;
    error?: string | null;
  }): string | null => {
    const topEntry = getTopModalEntry();

    if (!topEntry) {
      return null;
    }

    const normalizedWorkItemId = params.workItemId ? String(params.workItemId) : null;
    const normalizedWorkItemNumber = params.workItemNumber ? normalizeTargetValue(String(params.workItemNumber)) : null;
    const topEntryItemId = topEntry.workItem?.id ? String(topEntry.workItem.id) : null;
    const topEntryItemNumber = topEntry.workItem?.item_number
      ? normalizeTargetValue(String(topEntry.workItem.item_number))
      : null;

    const isSameTarget = topEntry.requestedTarget === params.requestedTarget
      || (normalizedWorkItemId !== null && topEntryItemId === normalizedWorkItemId)
      || (normalizedWorkItemNumber !== null && topEntryItemNumber === normalizedWorkItemNumber);

    if (!isSameTarget) {
      return null;
    }

    updateModalEntry(topEntry.key, {
      options: params.options,
      requestedTarget: params.requestedTarget,
      ...(params.workItem !== undefined ? { workItem: params.workItem } : {}),
      ...(params.isLoading !== undefined ? { isLoading: params.isLoading } : {}),
      ...(params.error !== undefined ? { error: params.error } : {}),
    });

    return topEntry.key;
  }, [getTopModalEntry, normalizeTargetValue, updateModalEntry]);

  const closeWorkItemByKey = useCallback((key?: string) => {
    let closingEntry: WorkItemModalStackEntry | null = null;
    let nextStackLength = 0;

    setModalStack((previousStack) => {
      const targetKey = key ?? previousStack[previousStack.length - 1]?.key;
      if (!targetKey) {
        return previousStack;
      }

      closingEntry = previousStack.find((entry) => entry.key === targetKey) || null;
      const nextStack = previousStack.filter((entry) => entry.key !== targetKey);
      nextStackLength = nextStack.length;
      return nextStack;
    });

    const finalizedClosingEntry = closingEntry as WorkItemModalStackEntry | null;

    if (finalizedClosingEntry) {
      finalizedClosingEntry.options.onClose?.();
    }

    if (nextStackLength === 0 && searchParams?.get('wi')) {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete('wi');
      const newUrl = newParams.toString()
        ? `${pathname}?${newParams.toString()}`
        : pathname || '/client-zone/dashboard';
      router.replace(newUrl, { scroll: false });
    }
  }, [pathname, router, searchParams]);

  // ============================================================================
  // ITEM RESOLUTION
  // Each item type now has its own visual identity in WorkItemDetailModal
  // (appointments = green, jobs = teal, etc.) so items open directly.
  // ============================================================================

  // ============================================================================
  // OPEN FUNCTIONS
  // ============================================================================

  const openWorkItem = useCallback(async (id: string, opts?: WorkItemOpenerOptions) => {
    const options = opts || {};
    const requestedTarget = createRequestedTarget('id', id);
    const reusedKey = reuseTopEntryIfSameTarget({
      requestedTarget,
      workItemId: id,
      options,
      isLoading: true,
      error: null,
    });

    if (reusedKey) {
      const topEntry = getTopModalEntry();
      if (topEntry?.requestedTarget === requestedTarget && topEntry.workItem) {
        return;
      }
    }

    const modalKey = createModalEntryKey();
    pushModalEntry({
      key: modalKey,
      workItem: null,
      isLoading: true,
      error: null,
      options,
      requestedTarget,
    });

    const { item, error } = await fetchWorkItem(id);
    updateModalEntry(modalKey, {
      workItem: item,
      isLoading: false,
      error,
      requestedTarget,
    });
  }, [createModalEntryKey, createRequestedTarget, fetchWorkItem, getTopModalEntry, pushModalEntry, reuseTopEntryIfSameTarget, updateModalEntry]);

  const openWorkItemByNumber = useCallback(async (itemNumber: string, opts?: WorkItemOpenerOptions) => {
    const options = opts || {};
    const requestedTarget = createRequestedTarget('number', itemNumber);
    const reusedKey = reuseTopEntryIfSameTarget({
      requestedTarget,
      workItemNumber: itemNumber,
      options,
      isLoading: true,
      error: null,
    });

    if (reusedKey) {
      const topEntry = getTopModalEntry();
      if (topEntry?.requestedTarget === requestedTarget && topEntry.workItem) {
        return;
      }
    }

    const modalKey = createModalEntryKey();
    pushModalEntry({
      key: modalKey,
      workItem: null,
      isLoading: true,
      error: null,
      options,
      requestedTarget,
    });

    const { item, error } = await fetchWorkItem(itemNumber);
    updateModalEntry(modalKey, {
      workItem: item,
      isLoading: false,
      error,
      requestedTarget,
    });
  }, [createModalEntryKey, createRequestedTarget, fetchWorkItem, getTopModalEntry, pushModalEntry, reuseTopEntryIfSameTarget, updateModalEntry]);

  const openWorkItemDirect = useCallback(async (item: WorkItem, opts?: WorkItemOpenerOptions) => {
    const options = opts || {};
    const directIdentifier = item.id || item.item_number;
    if (directIdentifier) {
      const requestedTarget = createRequestedTarget('direct', String(directIdentifier));
      const reusedKey = reuseTopEntryIfSameTarget({
        requestedTarget,
        workItemId: item.id,
        workItemNumber: item.item_number,
        options,
        workItem: item,
        isLoading: false,
        error: null,
      });

      if (reusedKey) {
        return;
      }

      pushModalEntry({
        key: createModalEntryKey(),
        workItem: item,
        isLoading: false,
        error: null,
        options,
        requestedTarget,
      });
      return;
    }

    pushModalEntry({
      key: createModalEntryKey(),
      workItem: item,
      isLoading: false,
      error: null,
      options,
    });
  }, [createModalEntryKey, createRequestedTarget, pushModalEntry, reuseTopEntryIfSameTarget]);

  const closeWorkItem = useCallback(() => {
    closeWorkItemByKey();
  }, [closeWorkItemByKey]);

  // ============================================================================
  // PERMALINK GENERATOR
  // ============================================================================

  const getPermalink = useCallback((workItemIdOrNumber: string): string => {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    return `${base}/client-zone/dashboard?wi=${encodeURIComponent(workItemIdOrNumber)}`;
  }, []);

  // ============================================================================
  // URL PARAM LISTENER — auto-open from ?wi=...
  // ============================================================================

  useEffect(() => {
    const wiParam = searchParams?.get('wi');
    if (!wiParam) {
      handledWiParam.current = null;
      return;
    }

    // Don't re-open the same param
    if (handledWiParam.current === wiParam) return;
    handledWiParam.current = wiParam;

    // Small delay to let the page render first
    const timer = setTimeout(() => {
      if (isUuid(wiParam)) {
        openWorkItem(wiParam);
      } else {
        openWorkItemByNumber(wiParam);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [searchParams, openWorkItem, openWorkItemByNumber]);

  // ============================================================================
  // GLOBAL EVENT LISTENER — components can dispatch 'openWorkItem' events
  // ============================================================================

  useEffect(() => {
    return on('openWorkItem', (detail) => {
      if (detail.workItem) openWorkItemDirect(detail.workItem as WorkItem, detail.options);
      else if (detail.id) openWorkItem(detail.id, detail.options);
      else if (detail.number) openWorkItemByNumber(detail.number, detail.options);
    });
  }, [openWorkItem, openWorkItemByNumber, openWorkItemDirect]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const topModalEntry = modalStack[modalStack.length - 1] || null;

  const value: WorkItemOpenerContextValue = {
    openWorkItem,
    openWorkItemByNumber,
    openWorkItemDirect,
    closeWorkItem,
    isOpen: modalStack.length > 0,
    currentWorkItem: topModalEntry?.workItem || null,
    isLoading: Boolean(topModalEntry?.isLoading),
    getPermalink,
  };

  return (
    <WorkItemOpenerContext.Provider value={value}>
      {children}

      {/* Global Work Item Detail Modals */}
      {modalStack.map((entry) => (
        entry.workItem && isFinanceDocumentWorkItem(entry.workItem) ? (
          <FinanceDocumentModal
            key={entry.key}
            isOpen={true}
            onClose={() => closeWorkItemByKey(entry.key)}
            document={mapWorkItemToFinanceDocument(entry.workItem)}
            doc_type={entry.workItem.item_type}
            config={DOCUMENT_CONFIGS[entry.workItem.item_type]}
            onSaved={() => {
              entry.options.onRefresh?.();
              emit('workItemCreated', undefined);
              closeWorkItemByKey(entry.key);
            }}
          />
        ) : entry.workItem ? (
          <WorkItemDetailModal
            key={entry.key}
            isOpen={true}
            onClose={() => closeWorkItemByKey(entry.key)}
            workItem={entry.workItem}
            initialTab={entry.options.initialTab}
            onRefresh={entry.options.onRefresh}
            visibleTabs={entry.options.visibleTabs}
            title={entry.options.title}
            onStatusChange={entry.options.onStatusChange}
            listModuleContext={entry.options.listModuleContext ?? null}
            activeSolutionKey={entry.options.activeSolutionKey ?? null}
          />
        ) : null
      ))}

      {/* Loading overlay when fetching */}
      {topModalEntry?.isLoading && (
        renderPortal(
          <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-600 dark:text-gray-300">Loading work item...</p>
            </div>
          </div>
        )
      )}

      {/* Error display */}
      {topModalEntry?.error && !topModalEntry.workItem && (
        renderPortal(
          <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 text-center">{topModalEntry.error}</p>
              <button
                onClick={closeWorkItem}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )
      )}
    </WorkItemOpenerContext.Provider>
  );
}

// ============================================================================
// PROVIDER (wraps children with Suspense for useSearchParams)
// ============================================================================

export function WorkItemOpenerProvider({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={null}>
      <WorkItemOpenerInner>{children}</WorkItemOpenerInner>
    </Suspense>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useWorkItemOpener(): WorkItemOpenerContextValue {
  const context = useContext(WorkItemOpenerContext);
  if (!context) {
    throw new Error('useWorkItemOpener must be used within a WorkItemOpenerProvider');
  }
  return context;
}

export function useOptionalWorkItemOpener(): WorkItemOpenerContextValue | null {
  return useContext(WorkItemOpenerContext) || null;
}

// ============================================================================
// UTILITY: Dispatch openWorkItem event from outside React tree
// ============================================================================

export function dispatchOpenWorkItem(params: { id?: string; number?: string; options?: WorkItemOpenerOptions }) {
  emit('openWorkItem', params);
}

// ============================================================================
// UTILITY: Generate permalink (static, doesn't need context)
// ============================================================================

export function generateWorkItemPermalink(itemNumberOrId: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/client-zone/dashboard?wi=${encodeURIComponent(itemNumberOrId)}`;
}
